import type { IClientMinimal } from "@/api";
import {
    asScenarioObject,
    getFeaturedAgents,
    getStarterQuestionText,
    hasInputSlots,
    requiredDocCount,
    type IAgentFeatured,
    type IScenarioObject,
    type StarterQuestion,
} from "@/api/agents";
import type { IAttachmentOption } from "@/api/attachments";
import type {
    ContentBlockStatus,
    ICitation,
    ICitationResolution,
    IContextEnrichmentDataPart,
} from "@/api/chat/chat.types";
import {
    abandonActiveChatTurn,
    abandonActiveConversationTurn,
} from "@/api/chat";
import { useIsChatSheetSurface } from "@/components/assistant-ui/chat-sheet-surface";
import { CitationResolutionProvider } from "@/components/assistant-ui/citation-resolution";
import {
    ComposerClientScopeContext,
    useComposerClientScope,
} from "@/components/assistant-ui/composer-client-scope";
import {
    MarkdownActionsProvider,
    useMarkdownActions,
    type MarkdownActionsContextValue,
} from "@/components/assistant-ui/markdown-actions";
import { EditorialMarkdownText } from "@/components/assistant-ui/markdown-text";
import { ToolFallback } from "@/components/assistant-ui/tool-fallback";
import { TooltipIconButton } from "@/components/assistant-ui/tooltip-icon-button";
import { useAssistantRichCopy } from "@/components/assistant-ui/useAssistantRichCopy";
import { resolveCitationLinks } from "@/components/assistant-ui/utils/citation-links";
import { handleAttachmentAction } from "@/components/shared/attachment-action";
import type { ReferenceItem } from "@/components/shared/attachment-pill-row.utils";
import { AttachmentPillRow } from "@/components/shared/AttachmentPillRow";
import { ClientCombobox } from "@/components/shared/ClientCombobox";
import {
    mapAuiAttachmentToBrief,
    toAuiAttachment,
} from "@/components/shared/user-message-attachment.utils";
import { Button } from "@/components/ui/button";
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { POLLY_DISCLAIMER_COPY } from "@/config/constants";
import { useAttachmentExportModals } from "@/hooks/useAttachmentExportModals";
import { useConversationClient } from "@/hooks/useConversationClient";
import { trackAgentShortcutClicked } from "@/lib/analytics";
import { cn } from "@/lib/utils";
import {
    forgetActiveTurn,
    resolveActiveTurn,
} from "@/utils/active-turn-identity";
import {
    setNextAgentId,
    setNextAttachments,
    setNextClientId,
} from "@/pages/frontend/chat/adapters/api-thread-list-adapter";
import {
    ChainOfThought,
    type ChainStep,
} from "@/pages/frontend/chat/components/ChainOfThought";
import { ComposerAttachmentMenu } from "@/pages/frontend/chat/components/composer/ComposerAttachmentMenu";
import { ContextEnrichmentCard } from "@/pages/frontend/chat/components/ContextEnrichmentCard";
import { ProcessingPart } from "@/pages/frontend/chat/components/ProcessingPart";
import { ReferencesDataPart } from "@/pages/frontend/chat/components/ReferencesDataPart";
import { RecoveringMessageSlot } from "@/pages/frontend/chat/components/RecoveringMessageSlot";
import { RecoveryRetryButton } from "@/pages/frontend/chat/components/RecoveryRetryButton";
import { ReportCard } from "@/pages/frontend/chat/components/ReportCard";
import { useThreadSwitchHold } from "@/pages/frontend/chat/components/runtime-provider.thread-target";
import { TemplateRenderCard } from "@/pages/frontend/chat/components/TemplateRenderCard";
import { useDocumentViewer } from "@/pages/frontend/chat/contexts/useDocumentViewer";
import { useStageAttachments } from "@/pages/frontend/chat/hooks/useStageAttachments";
import { getAttachmentClientId } from "@/pages/frontend/chat/utils/attachment-client-registry";
import {
    ActionBarMorePrimitive,
    ActionBarPrimitive,
    AuiIf,
    ComposerPrimitive,
    ErrorPrimitive,
    MessagePrimitive,
    ThreadPrimitive,
    useAui,
    useAuiState,
} from "@assistant-ui/react";
import { useQuery } from "@tanstack/react-query";
import {
    ArrowDownIcon,
    ArrowUpIcon,
    BrainIcon,
    CheckIcon,
    ChevronDownIcon,
    CopyIcon,
    DownloadIcon,
    Loader2Icon,
    MoreHorizontalIcon,
    PaperclipIcon,
    SquareIcon,
} from "lucide-react";
import {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    type FC,
    type ReactNode,
} from "react";
import { useParams } from "react-router";
import { getAuthSessionEpoch } from "@/contexts/auth/AuthContext";
import { useAuth } from "@/contexts/auth/useAuth";
import { OrganizationContext } from "@/contexts/frontend/organization/OrganizationContext";
import { InputGateModal } from "./InputGateModal";
import { MessageSenderChip } from "./MessageSenderChip";
import { WorkflowPicker } from "./WorkflowPicker";

export const Thread: FC = () => {
    const isSwitching = useThreadSwitchHold();

    return (
        <ThreadPrimitive.Root
            className="aui-root aui-thread-root bg-background @container flex h-full flex-col"
            style={{
                ["--thread-max-width" as string]: "48rem",
                ["--composer-radius" as string]: "24px",
                ["--composer-padding" as string]: "10px",
            }}
        >
            <ThreadPrimitive.Viewport
                turnAnchor="top"
                className="aui-thread-viewport relative flex flex-1 flex-col overflow-x-hidden overflow-y-scroll scroll-smooth px-4 pt-4 [scrollbar-gutter:stable_both-edges]"
            >
                <ThreadSwitchStatus isSwitching={isSwitching} />

                {isSwitching ? null : (
                    <>
                        <ThreadWelcomeOverlay />

                        <AuiIf condition={(s) => !s.thread.isEmpty}>
                            <div
                                data-testid="thread-populated-layout"
                                className="contents"
                            >
                                <ThreadPrimitive.Messages>
                                    {() => <ThreadMessage />}
                                </ThreadPrimitive.Messages>
                            </div>
                        </AuiIf>
                    </>
                )}

                <ComposerFooter />
            </ThreadPrimitive.Viewport>
        </ThreadPrimitive.Root>
    );
};

const ThreadSwitchStatus: FC<{ isSwitching: boolean }> = ({ isSwitching }) => (
    <div
        role="status"
        className={cn(
            isSwitching &&
                "text-muted-foreground flex flex-1 items-center justify-center gap-2 text-sm"
        )}
    >
        {isSwitching ? (
            <>
                <Loader2Icon aria-hidden className="size-4 animate-spin" />
                Loading chat
            </>
        ) : null}
    </div>
);

const ThreadWelcomeOverlay: FC = () => {
    const isEmpty = useAuiState((s) => s.thread.isEmpty);

    return (
        <div
            data-testid="thread-welcome-overlay"
            inert={isEmpty ? undefined : true}
            className={cn(
                "flex w-full shrink-0 flex-col items-center justify-end px-4 transition-opacity duration-200",
                isEmpty
                    ? "pointer-events-auto min-h-[calc(100%-50vh)] opacity-100"
                    : "pointer-events-none h-0 overflow-hidden opacity-0"
            )}
        >
            <ThreadWelcome />
        </div>
    );
};

const ComposerFooter: FC = () => {
    const isEmpty = useAuiState((s) => s.thread.isEmpty);

    return (
        <ThreadPrimitive.ViewportFooter
            className={cn(
                "aui-thread-viewport-footer bg-background z-20 mx-auto flex w-full max-w-(--thread-max-width) flex-col gap-4 overflow-visible rounded-t-(--composer-radius) pb-2",
                !isEmpty && "sticky bottom-0 mt-auto"
            )}
        >
            <ThreadScrollToBottom />
            <Composer />
        </ThreadPrimitive.ViewportFooter>
    );
};

const ThreadMessage: FC = () => {
    const role = useAuiState((s) => s.message.role);
    const isEditing = useAuiState((s) => s.message.composer.isEditing);
    if (isEditing) return <EditComposer />;
    if (role === "user") return <UserMessage />;
    return <AssistantMessage />;
};

const ThreadScrollToBottom: FC = () => {
    return (
        <ThreadPrimitive.ScrollToBottom
            render={
                <TooltipIconButton
                    tooltip="Scroll to bottom"
                    variant="outline"
                    className="aui-thread-scroll-to-bottom dark:border-border dark:bg-background dark:hover:bg-accent absolute -top-12 z-10 self-center rounded-full p-4 disabled:invisible"
                />
            }
        >
            <ArrowDownIcon />
        </ThreadPrimitive.ScrollToBottom>
    );
};

export const ThreadFeaturedAgents: FC = () => {
    const { data: featuredAgents = [] } = useQuery({
        queryKey: ["featured-agents"],
        queryFn: getFeaturedAgents,
    });
    const [selectedAgentId, setSelectedAgentId] = useState<number | null>(null);
    const mainThreadId = useAuiState((s) => s.threads.mainThreadId);
    const selectedAgent =
        featuredAgents.find(
            (agent) =>
                agent.id === selectedAgentId &&
                Boolean(agent.starter_questions?.length)
        ) ?? null;

    useEffect(() => {
        setSelectedAgentId(null);
    }, [mainThreadId]);

    useEffect(() => {
        if (selectedAgentId !== null && selectedAgent === null) {
            setSelectedAgentId(null);
        }
    }, [selectedAgent, selectedAgentId]);

    const handleAgentClick = (agent: IAgentFeatured) => {
        trackAgentShortcutClicked({ agent_id: agent.id });
        if (!agent.starter_questions?.length) return;
        setSelectedAgentId(agent.id);
    };

    const handleClear = () => setSelectedAgentId(null);

    if (featuredAgents.length === 0) return null;

    return (
        <div className="fade-in slide-in-from-bottom-2 animate-in fill-mode-both mt-6 mb-4 w-full motion-reduce:animate-none">
            <WorkflowPicker
                workflows={featuredAgents}
                selectedWorkflow={selectedAgent}
                onSelect={handleAgentClick}
                onClear={handleClear}
            >
                {selectedAgent && <ThreadSuggestions agent={selectedAgent} />}
            </WorkflowPicker>
        </div>
    );
};

const ThreadWelcome: FC = () => {
    return (
        <div className="aui-thread-welcome-root mx-auto flex w-full max-w-(--thread-max-width) flex-col items-center">
            <div className="aui-thread-welcome-center flex w-full flex-col items-center gap-1">
                <div className="aui-thread-welcome-message flex flex-col items-center gap-1 px-4 text-center">
                    <h1 className="aui-thread-welcome-message-inner fade-in slide-in-from-bottom-1 animate-in fill-mode-both text-2xl font-semibold duration-200">
                        Hi, I'm Polly!
                    </h1>
                    <p className="aui-thread-welcome-message-inner fade-in slide-in-from-bottom-1 animate-in fill-mode-both text-muted-foreground text-lg delay-75 duration-200">
                        What are you working on?
                    </p>
                </div>
                <ThreadFeaturedAgents />
            </div>
        </div>
    );
};

const ThreadSuggestions: FC<{ agent: IAgentFeatured }> = ({ agent }) => {
    const aui = useAui();
    const [gate, setGate] = useState<{
        agentId: number;
        agentName: string;
        scenario: IScenarioObject;
    } | null>(null);
    // Re-entry guard so a double-click on the gate's Start stages + appends
    // (and creates a conversation) exactly once before the unmount lands.
    const submittedRef = useRef(false);
    const mainThreadId = useAuiState((s) => s.threads.mainThreadId);

    useEffect(() => {
        setGate(null);
    }, [mainThreadId]);

    const handleStarterClick = useCallback(
        (agentId: number, agentName: string, question: StarterQuestion) => {
            if (hasInputSlots(question)) {
                submittedRef.current = false;
                setGate({
                    agentId,
                    agentName,
                    scenario: asScenarioObject(question),
                });
                return;
            }
            setNextAgentId(agentId, agentName);
            const composer = aui.thread().composer();
            const pendingAttachments = composer.getState().attachments;
            const attachments = pendingAttachments.map((a) => ({
                ...a,
                status: { type: "complete" as const },
                content: a.content ?? [],
            }));
            aui.thread().append({
                role: "user",
                content: [
                    { type: "text", text: getStarterQuestionText(question) },
                ],
                attachments: attachments.length > 0 ? attachments : undefined,
            });
            if (attachments.length > 0) {
                composer.clearAttachments();
            }
        },
        [aui]
    );

    const handleGateStart = useCallback(
        (collected: IAttachmentOption[]) => {
            if (submittedRef.current || !gate) return;
            submittedRef.current = true;
            // initialize() reads this side channel synchronously, so preserve
            // authored slot/pick order and stage it before appending.
            setNextAgentId(gate.agentId, gate.agentName);
            setNextAttachments(collected);
            aui.thread().append({
                role: "user",
                content: [{ type: "text", text: gate.scenario.question }],
                attachments: collected.map(toAuiAttachment),
            });
            setGate(null);
        },
        [aui, gate]
    );

    const handleGateClose = useCallback(() => setGate(null), []);

    return (
        <>
            <div className="mt-2 grid w-full grid-cols-1 gap-2 @md:grid-cols-2">
                {agent.starter_questions?.map((question, i) => {
                    const text = getStarterQuestionText(question);
                    const docCount = requiredDocCount(question);
                    const hint =
                        docCount > 0
                            ? `${docCount} document${docCount > 1 ? "s" : ""} needed`
                            : "Ask directly";
                    return (
                        <Button
                            variant="outline"
                            key={i}
                            className="aui-thread-welcome-suggestion bg-background hover:bg-primary/10 h-auto w-full items-start justify-start gap-1 rounded-lg border px-4 py-3 text-left text-sm whitespace-normal transition-colors motion-reduce:transition-none"
                            onClick={() =>
                                handleStarterClick(
                                    agent.id,
                                    agent.name,
                                    question
                                )
                            }
                        >
                            <span className="flex w-full flex-col items-start gap-1">
                                <span className="font-medium">{text}</span>
                                <span className="text-muted-foreground flex items-center gap-1 text-xs">
                                    {docCount > 0 && (
                                        <PaperclipIcon
                                            className="size-3 shrink-0"
                                            aria-hidden="true"
                                        />
                                    )}
                                    {hint}
                                </span>
                            </span>
                        </Button>
                    );
                })}
            </div>
            {gate && (
                <InputGateModal
                    key={`${gate.agentId}:${gate.scenario.question}`}
                    open
                    scenario={gate.scenario}
                    onStart={handleGateStart}
                    onClose={handleGateClose}
                />
            )}
        </>
    );
};

const ComposerAttachmentPills: FC = () => {
    const aui = useAui();
    const auiAttachments = useAuiState((s) => s.composer.attachments) ?? [];
    const remoteId = useAuiState((s) => {
        const id = s.threads.mainThreadId;
        return s.threads.threadItems.find((t) => t.id === id)?.remoteId;
    });
    // Conversation scope for the cross-client warning: an existing
    // conversation's client wins; the composer-staged selection covers
    // brand-new threads where no conversation row exists yet. Both
    // unknown → unscoped → no warnings (fail-quiet, like the header chip).
    const { clientId: conversationClientId } = useConversationClient(remoteId);
    const { stagedClient } = useComposerClientScope();
    const scopeClientId = conversationClientId ?? stagedClient?.id;

    if (auiAttachments.length === 0) return null;

    const handleRemove = (ref: ReferenceItem) => {
        aui.thread()
            .composer()
            .attachment({ id: `${ref.type}:${ref.id}` })
            .remove();
    };

    // Advisory only — a mismatched attachment stays attached and sendable
    // (explicit attachments pierce the retrieval fence server-side).
    const warningFor = (ref: ReferenceItem): string | undefined => {
        if (scopeClientId == null) return undefined;
        const attachmentClientId = getAttachmentClientId(
            `${ref.type}:${ref.id}`
        );
        if (attachmentClientId == null || attachmentClientId === scopeClientId)
            return undefined;
        return "Belongs to another client — outside this conversation's scope";
    };

    return (
        <div className="w-full px-1 empty:hidden">
            <AttachmentPillRow
                citations={[]}
                attachments={auiAttachments.map(mapAuiAttachmentToBrief)}
                onRemove={handleRemove}
                warningFor={warningFor}
            />
        </div>
    );
};

export const Composer: FC = () => {
    // On page refresh, react-textarea-autosize measures the textarea height in
    // useLayoutEffect before react-resizable-panels has computed the panel width.
    // The textarea width is 0 at that point, causing the placeholder to wrap
    // char-by-char and produce an enormous height (e.g. 328px). Forcing a
    // re-render after mount triggers a recalculation with the correct width.
    const [, setMounted] = useState(false);
    useEffect(() => setMounted(true), []);

    // Stage composer attachments on the api-thread-list-adapter
    // side-channel so the next `initialize()` (fresh chat) forwards them
    // to `POST /chat/conversations`. The backend's existing
    // `maybe_auto_tag_from_attachments` then sets `client_id` on the new
    // row in the same transaction, landing the chip in the first
    // conversation-detail fetch.
    useStageAttachments();

    // Mirror of the selector's staged client so siblings (the pill row's
    // cross-client warning) can read the pending scope before the
    // conversation exists. The adapter side-channel remains the value
    // initialize() actually consumes.
    const [stagedClient, setStagedClient] = useState<IClientMinimal | null>(
        null
    );
    const clientScope = useMemo(
        () => ({ stagedClient, setStagedClient }),
        [stagedClient]
    );
    const isSwitching = useThreadSwitchHold();

    return (
        <ComposerClientScopeContext.Provider value={clientScope}>
            <ComposerPrimitive.Root className="aui-composer-root relative flex w-full flex-col">
                <fieldset disabled={isSwitching} className="contents">
                    <div className="bg-background focus-within:border-ring/75 focus-within:ring-ring/20 data-[dragging=true]:border-ring data-[dragging=true]:bg-accent/50 mb-2 flex w-full flex-col gap-2 rounded-(--composer-radius) border p-(--composer-padding) transition-shadow focus-within:ring-2 data-[dragging=true]:border-dashed">
                        <ComposerAttachmentPills />
                        <ComposerPrimitive.Input
                            placeholder="Let me help you..."
                            className="max-h-[40dvh] min-h-10 w-full resize-none overflow-y-auto bg-transparent px-1.75 py-1 text-sm focus:outline-none"
                            rows={1}
                            autoFocus
                            aria-label="Message input"
                        />
                        <ComposerAction />
                    </div>
                    <AuiIf condition={(s) => !s.thread.isEmpty}>
                        <p className="text-center text-xs text-gray-700 italic">
                            {POLLY_DISCLAIMER_COPY}
                        </p>
                    </AuiIf>
                </fieldset>
            </ComposerPrimitive.Root>
        </ComposerClientScopeContext.Provider>
    );
};

/**
 * New-thread-only client picker. Selection stages on the adapter
 * side-channel (`setNextClientId`) so the first send's `initialize()`
 * creates the conversation scoped to that client. Hidden inside
 * ChatSheet, which pre-stages its own client (see chat-sheet-surface).
 * The hide/state split lives in two components so the sheet branch
 * mounts zero hooks/effects.
 */
const ComposerClientSelector: FC = () => {
    const isChatSheet = useIsChatSheetSurface();
    if (isChatSheet) return null;
    return <ComposerClientSelectorInner />;
};

const ComposerClientSelectorInner: FC = () => {
    // Selection state lives in the composer's client-scope context (not
    // locally) so the pill row can warn on cross-client attachments
    // before the conversation exists.
    const { stagedClient, setStagedClient } = useComposerClientScope();

    const handleChange = useCallback(
        (c: IClientMinimal | null) => {
            setStagedClient(c);
            setNextClientId(c?.id);
        },
        [setStagedClient]
    );

    // Unmount = leaving the new-thread composer without sending (thread
    // switch / navigation): clear the stage so an unsent selection can't
    // tag an unrelated future conversation. Post-send unmounts are
    // no-ops — initialize() consumes-and-clears synchronously inside the
    // send call stack, before React runs this cleanup.
    useEffect(
        () => () => {
            setNextClientId(undefined);
            setStagedClient(null);
        },
        [setStagedClient]
    );

    return (
        <div data-testid="composer-client-selector" className="max-w-56">
            <ClientCombobox
                value={stagedClient}
                onChange={handleChange}
                placeholder="All Clients"
                allOptionLabel="All Clients"
            />
        </div>
    );
};

const ComposerAction: FC = () => {
    return (
        <div className="flex items-center justify-between">
            <div className="flex items-center gap-1">
                <ComposerAttachmentMenu />
                <AuiIf condition={(s) => s.thread.isEmpty}>
                    <ComposerClientSelector />
                </AuiIf>
            </div>
            <AuiIf condition={(s) => !s.thread.isRunning}>
                <ComposerPrimitive.Send
                    render={
                        <TooltipIconButton
                            tooltip="Send message"
                            side="bottom"
                            type="button"
                            variant="default"
                            size="icon"
                            className="aui-composer-send hover:bg-primary/80 size-8 rounded-full"
                            aria-label="Send message"
                        />
                    }
                >
                    <ArrowUpIcon className="aui-composer-send-icon size-4" />
                </ComposerPrimitive.Send>
            </AuiIf>
            <AuiIf condition={(s) => s.thread.isRunning}>
                <ComposerStop />
            </AuiIf>
        </div>
    );
};

export const ComposerStop: FC = () => {
    const aui = useAui();
    const remoteId = useAuiState((s) => s.threadListItem.remoteId);
    const stoppingRef = useRef(false);
    const { user } = useAuth();
    const organizationId =
        useContext(OrganizationContext)?.organization?.id ?? null;

    const currentRunId = useCallback(() => {
        const messages = aui.thread().getState().messages;
        return messages.length > 0 ? messages[messages.length - 1].id : null;
    }, [aui]);

    const stop = useCallback(async () => {
        if (!remoteId || stoppingRef.current) return;
        const scope = {
            userId: user?.id ?? null,
            organizationId,
            conversationId: remoteId,
            authSessionEpoch: getAuthSessionEpoch(),
        };
        const active = resolveActiveTurn(scope);
        const runAtRequest = currentRunId();
        stoppingRef.current = true;
        try {
            // A turn held under another principal, organization, or auth
            // session is not this scope's to end: two organizations can hold
            // the same conversation id, so the conversation-wide fallback
            // would abandon a stranger's turn.
            if (active.kind === "stale") {
                forgetActiveTurn(scope);
            } else if (active.kind === "none") {
                await abandonActiveConversationTurn(remoteId);
            } else {
                await abandonActiveChatTurn(
                    remoteId,
                    active.assistantMessageId
                );
            }
        } catch {
            if (currentRunId() !== runAtRequest) return;
        } finally {
            stoppingRef.current = false;
        }
        if (currentRunId() !== runAtRequest) return;
        aui.composer().cancel();
    }, [aui, currentRunId, remoteId, user?.id, organizationId]);

    if (!remoteId) return null;

    return (
        <Button
            type="button"
            variant="default"
            size="icon"
            className="aui-composer-cancel hover:bg-primary/80 size-8 rounded-full"
            aria-label="Stop generating"
            onClick={() => void stop()}
        >
            <SquareIcon className="aui-composer-cancel-icon size-3 fill-current" />
        </Button>
    );
};

const MessageError: FC = () => {
    return (
        <MessagePrimitive.Error>
            <ErrorPrimitive.Root className="aui-message-error-root border-destructive bg-destructive/10 text-destructive dark:bg-destructive/5 mt-2 rounded-md border p-3 text-sm dark:text-red-200">
                <ErrorPrimitive.Message className="aui-message-error-message line-clamp-2" />
            </ErrorPrimitive.Root>
        </MessagePrimitive.Error>
    );
};

export const MESSAGE_CANCELLED_COPY =
    "You cancelled this response. Ask again to get a fresh one.";

/**
 * Explains a turn the user cancelled before anything was written.
 *
 * `convertMessages` maps a cancelled turn to `incomplete/cancelled`, which
 * `MessagePrimitive.Error` deliberately ignores: stopping mid-stream leaves the
 * partial prose on screen, and an error panel over it would contradict what the
 * user just watched. A queued turn cancelled before it started has no prose, so
 * that same choice leaves an empty bubble explaining nothing.
 *
 * Chromeless and muted rather than the destructive panel, because the user
 * asked for this -- it is an outcome, not a failure.
 */
const MessageCancelledNotice: FC = () => {
    const cancelledWithNothingToShow = useAuiState((s) => {
        const status = s.message.status;
        if (status?.type !== "incomplete" || status.reason !== "cancelled") {
            return false;
        }
        // Trimmed, unlike the copy affordance's identical-looking check: a
        // whitespace-only turn still renders as a blank bubble, which is the
        // state this notice exists to explain.
        return !s.message.parts.some(
            (part) => part.type === "text" && part.text.trim().length > 0
        );
    });

    if (!cancelledWithNothingToShow) return null;

    return (
        <div
            data-slot="message-cancelled"
            role="status"
            aria-live="polite"
            className="aui-message-cancelled text-muted-foreground fade-in animate-in mt-2 text-sm duration-200"
        >
            {MESSAGE_CANCELLED_COPY}
        </div>
    );
};

const ReasoningBlock: FC<{ text: string }> = ({ text }) => {
    const [open, setOpen] = useState(false);
    const preview = text.length > 200 ? text.slice(0, 200) + "…" : text;

    return (
        <Collapsible open={open} onOpenChange={setOpen} className="my-2">
            <CollapsibleTrigger className="group/reasoning border-border/60 bg-muted/40 text-muted-foreground hover:bg-muted/70 flex w-full items-center gap-2 rounded-lg border px-3 py-2 text-left text-xs transition-colors">
                <BrainIcon className="text-primary/60 size-3.5 shrink-0" />
                <span className="flex-1 truncate font-medium">
                    {open ? "Thinking" : preview}
                </span>
                <ChevronDownIcon
                    className={cn(
                        "size-3.5 shrink-0 transition-transform duration-200",
                        open && "rotate-180"
                    )}
                />
            </CollapsibleTrigger>
            <CollapsibleContent className="data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down overflow-hidden">
                <div className="border-border/40 bg-muted/20 text-muted-foreground mt-1 rounded-lg border border-dashed px-3 py-2 text-xs leading-relaxed">
                    {text}
                </div>
            </CollapsibleContent>
        </Collapsible>
    );
};

// ---------------------------------------------------------------------------
// Data part renderers: prop-less components that read directly from
// the assistant-ui store via useAuiState. This bypasses the
// RenderChildrenWithAccessor lazy cache for reliable streaming updates.
// ---------------------------------------------------------------------------

interface DataPart {
    type: "data";
    data: Record<string, unknown>;
}

const ChainOfThoughtDataPart: FC = () => {
    const part = useAuiState((s) => s.part);
    const isStreaming = useAuiState(
        (s) => s.message.status?.type === "running"
    );
    if (part.type !== "data") return null;
    const data = (part as DataPart).data ?? {};
    return (
        <ChainOfThought
            steps={(data.steps as ChainStep[]) ?? []}
            isStreaming={!!(isStreaming && data.isLastGroup)}
        />
    );
};

const ProcessingDataPart: FC = () => {
    const part = useAuiState((s) => s.part);
    if (part.type !== "data") return null;
    const data = (part as DataPart).data ?? {};
    return (
        <ProcessingPart
            name={(data.name as string) ?? ""}
            status={data.status as ContentBlockStatus}
            summary_text={data.summary_text as string}
        />
    );
};

const ReferencesDataPartWrapper: FC = () => {
    const part = useAuiState((s) => s.part);
    if (part.type !== "data") return null;
    return <ReferencesDataPart data={(part as DataPart).data ?? {}} />;
};

/**
 * Runtime guard on the data-part shape emitted by both backend paths.
 * Strict enough to reject half-formed payloads (e.g. from stale backend
 * versions) but lenient on optional fields like `messageId`.
 */
const isContextEnrichmentData = (
    data: unknown
): data is IContextEnrichmentDataPart => {
    if (typeof data !== "object" || data === null) return false;
    const d = data as Record<string, unknown>;
    return (
        Array.isArray(d.chips) &&
        typeof d.freeTextPrompt === "string" &&
        typeof d.dismissed === "boolean" &&
        typeof d.refined === "boolean" &&
        typeof d.isLatestActionable === "boolean"
    );
};

const ContextEnrichmentDataPart: FC = () => {
    const part = useAuiState((s) => s.part);
    const isStreaming = useAuiState(
        (s) => s.message.status?.type === "running"
    );
    if (part.type !== "data") return null;
    const raw = (part as DataPart).data;
    if (!isContextEnrichmentData(raw)) return null;
    // conversationId + messageId must both be present — without either,
    // the card can't reach the dismiss / refine endpoints. Both are
    // populated on live stream (the block envelope now carries
    // `message_id`; see `service.py`) and on rehydration (via the
    // persisted assistant-message row id). The guard stays as a
    // belt-and-braces defence against backend/deploy skew: older
    // servers that don't yet emit `message_id` degrade to not-rendered
    // so the user never clicks a button that would fail with "Missing
    // messageId".
    if (raw.conversationId === undefined || raw.messageId === undefined)
        return null;
    return (
        <ContextEnrichmentCard
            data={raw}
            conversationId={raw.conversationId}
            isStreaming={isStreaming}
        />
    );
};

// ---------------------------------------------------------------------------
// AssistantMessage (Components API for reliable store subscriptions)
// ---------------------------------------------------------------------------

const isTemplateRenderData = (
    data: unknown
): data is { conversationId: number; messageId: number } => {
    if (typeof data !== "object" || data === null) return false;
    const d = data as Record<string, unknown>;
    return (
        typeof d.conversationId === "number" && typeof d.messageId === "number"
    );
};

const TemplateRenderDataPart: FC = () => {
    const part = useAuiState((s) => s.part);
    if (part.type !== "data") return null;
    const raw = (part as DataPart).data;
    if (!isTemplateRenderData(raw)) return null;
    return (
        <TemplateRenderCard
            conversationId={raw.conversationId}
            messageId={raw.messageId}
        />
    );
};

const isReportData = (
    data: unknown
): data is { conversationId: number; chatMessageId: number } => {
    if (typeof data !== "object" || data === null) return false;
    const d = data as Record<string, unknown>;
    return (
        typeof d.conversationId === "number" &&
        typeof d.chatMessageId === "number"
    );
};

const ReportDataPart: FC = () => {
    const part = useAuiState((s) => s.part);
    if (part.type !== "data") return null;
    const raw = (part as DataPart).data;
    if (!isReportData(raw)) return null;
    return (
        <ReportCard
            conversationId={raw.conversationId}
            chatMessageId={raw.chatMessageId}
        />
    );
};

// Polly's answers are typeset; nothing else in the app is. The policy is
// chosen by WHICH component fills the slot, and this is the only mount that
// chooses the editorial one — the chat page and the chat sheet both render
// this same Thread, and the sheet is itself embedded across the app, so one
// line dispositions every surface. Shared chats need no gate because they are
// served by the same chat route. A fault here falls open to the plain path per
// message rather than blanking the thread.
const assistantMessageComponents = {
    Text: EditorialMarkdownText,
    Reasoning: ReasoningBlock,
    tools: { Fallback: ToolFallback },
    data: {
        by_name: {
            "chain-of-thought": ChainOfThoughtDataPart,
            processing: ProcessingDataPart,
            references: ReferencesDataPartWrapper,
            "context-enrichment": ContextEnrichmentDataPart,
            "template-render": TemplateRenderDataPart,
            report: ReportDataPart,
        },
    },
};

// Per-message citation wiring. The shared MarkdownActionsBridge can't see a
// message's handle->resolution map (handles reset each turn), so this scopes a
// resolution-aware onSource to the message: a cite handle opens the viewer at
// the resolved page with its bounding box highlighted; everything else
// delegates to the surrounding actions unchanged.
export const CitationSourceActions: FC<{
    resolutions: ICitationResolution[] | undefined;
    children: ReactNode;
}> = ({ resolutions, children }) => {
    const outer = useMarkdownActions();
    const { openDocument } = useDocumentViewer();

    const actions = useMemo<MarkdownActionsContextValue>(
        () => ({
            ...outer,
            onSource: (documentId, page, handle) => {
                const resolution = handle
                    ? resolutions?.find((r) => r.handle === handle)
                    : undefined;
                if (resolution) {
                    const citation: ICitation = {
                        document_id: resolution.document_id,
                        document_name: "",
                        document_url: "",
                        pages:
                            resolution.page != null
                                ? String(resolution.page)
                                : "",
                        highlight_areas: resolution.bbox ?? [],
                        scopes: [],
                    };
                    openDocument(citation, resolution.page ?? undefined);
                    return;
                }
                outer.onSource(documentId, page, handle);
            },
        }),
        [outer, resolutions, openDocument]
    );

    return (
        <MarkdownActionsProvider value={actions}>
            {children}
        </MarkdownActionsProvider>
    );
};

export const AssistantMessage: FC = () => {
    const citationResolutions = useAuiState(
        (s) =>
            s.message.metadata?.custom?.citation_resolutions as
                | ICitationResolution[]
                | undefined
    );
    // A recovering turn renders with `running` status, which would add the
    // streaming ● cursor on top of the RecoveringMessage affordance. The
    // attribute lets index.css suppress that cursor so recovery shows exactly
    // one progress indicator.
    const isRecovering = useAuiState(
        (s) => s.message.metadata?.custom?.recovery_display === "recovering"
    );
    return (
        <MessagePrimitive.Root
            className="aui-assistant-message-root fade-in slide-in-from-bottom-1 animate-in relative mx-auto w-full max-w-(--thread-max-width) py-3 duration-150"
            data-role="assistant"
            data-recovering={isRecovering || undefined}
        >
            <div className="aui-assistant-message-content text-foreground px-2 leading-relaxed wrap-break-word">
                <CitationResolutionProvider resolutions={citationResolutions}>
                    <CitationSourceActions resolutions={citationResolutions}>
                        <MessagePrimitive.Parts
                            components={assistantMessageComponents}
                        />
                    </CitationSourceActions>
                </CitationResolutionProvider>
                <MessageError />
                <MessageCancelledNotice />
                <RecoveringMessageSlot />
            </div>

            <div className="aui-assistant-message-footer mt-1 ml-2 flex min-h-6 items-center">
                <AssistantActionBar />
            </div>
        </MessagePrimitive.Root>
    );
};

/** Resolve a single message's cite:// markers and download it as markdown.
 * `ExportMarkdown`'s `onExport` hands us the raw copy text; we rewrite the
 * citations to openable deep-links so the saved file's links work (the
 * primitive's default writes the markers verbatim). */
function downloadMessageMarkdown(
    content: string,
    resolutions: ICitationResolution[] | undefined,
    chatId: string | undefined
): void {
    const md = resolveCitationLinks(content, resolutions, {
        chatId,
        appHost: window.location.origin,
    });
    const blob = new Blob([md], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `message-${Date.now()}.md`;
    a.click();
    URL.revokeObjectURL(url);
}

const AssistantCopyButton: FC<{
    chatId: string | undefined;
    resolutions: ICitationResolution[] | undefined;
}> = ({ chatId, resolutions }) => {
    const { copy, disabled } = useAssistantRichCopy({ chatId, resolutions });
    return (
        <TooltipIconButton
            tooltip="Copy"
            disabled={disabled}
            onClick={() => copy()}
        >
            <AuiIf condition={(s) => s.message.isCopied}>
                <CheckIcon />
            </AuiIf>
            <AuiIf condition={(s) => !s.message.isCopied}>
                <CopyIcon />
            </AuiIf>
        </TooltipIconButton>
    );
};

const AssistantActionBar: FC = () => {
    const { chatId } = useParams<{ chatId: string }>();
    const resolutions = useAuiState(
        (s) =>
            s.message.metadata?.custom?.citation_resolutions as
                | ICitationResolution[]
                | undefined
    );
    return (
        <ActionBarPrimitive.Root
            hideWhenRunning
            autohide="not-last"
            className="aui-assistant-action-bar-root text-muted-foreground col-start-3 row-start-2 -ml-1 flex gap-1"
        >
            <AssistantCopyButton chatId={chatId} resolutions={resolutions} />
            <RecoveryRetryButton />
            <ActionBarMorePrimitive.Root>
                <ActionBarMorePrimitive.Trigger asChild>
                    <TooltipIconButton
                        tooltip="More"
                        className="data-[state=open]:bg-accent"
                    >
                        <MoreHorizontalIcon />
                    </TooltipIconButton>
                </ActionBarMorePrimitive.Trigger>
                <ActionBarMorePrimitive.Content
                    side="bottom"
                    align="start"
                    className="aui-action-bar-more-content bg-popover text-popover-foreground z-50 min-w-32 overflow-hidden rounded-md border p-1 shadow-md"
                >
                    <ActionBarPrimitive.ExportMarkdown
                        onExport={(content) =>
                            downloadMessageMarkdown(
                                content,
                                resolutions,
                                chatId
                            )
                        }
                        render={
                            <ActionBarMorePrimitive.Item className="aui-action-bar-more-item hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none select-none" />
                        }
                    >
                        <DownloadIcon className="size-4" />
                        Export as Markdown
                    </ActionBarPrimitive.ExportMarkdown>
                </ActionBarMorePrimitive.Content>
            </ActionBarMorePrimitive.Root>
        </ActionBarPrimitive.Root>
    );
};

/**
 * Reads the full attachment array from message state rather than iterating via
 * `<MessagePrimitive.Attachments>{() => <Pill />}</MessagePrimitive.Attachments>`.
 * The iterator primitive renders one child per attachment with no count/index
 * context, which is incompatible with `AttachmentPillRow`'s overflow logic
 * (needs `attachments.length` up front to decide what to truncate into the
 * "+N more" popover). Reading the array once via `useAuiState` keeps overflow
 * rendering authoritative inside a single component and lets the shared row
 * stay purely presentational. Switching back to the iterator would silently
 * break overflow — both surfaces (here and `ReferencesDataPart`) pass the
 * full list into `AttachmentPillRow` for this reason.
 */
const UserMessageAttachments: FC = () => {
    const attachments = useAuiState((s) => s.message.attachments) ?? [];
    const { openDocument } = useDocumentViewer();
    const { triggerScopeExport, triggerComparisonExport, exportModalsElement } =
        useAttachmentExportModals();

    if (attachments.length === 0) return null;

    const mapped = attachments.map(mapAuiAttachmentToBrief);

    return (
        <>
            <div className="col-span-full col-start-1 row-start-1 flex w-full empty:hidden">
                {/* User-message attachments never carry server citations; view-highlights
                    only renders when ref.citation is set, so passing [] is safe. */}
                <AttachmentPillRow
                    citations={[]}
                    attachments={mapped}
                    onAction={(ref, action) =>
                        handleAttachmentAction(ref, action, {
                            openDocument,
                            onExportScope: triggerScopeExport,
                            onExportComparison: triggerComparisonExport,
                        })
                    }
                />
            </div>
            {exportModalsElement}
        </>
    );
};

const UserMessage: FC = () => {
    return (
        <MessagePrimitive.Root
            className="aui-user-message-root fade-in slide-in-from-bottom-1 animate-in mx-auto grid w-full max-w-(--thread-max-width) auto-rows-auto grid-cols-[minmax(72px,1fr)_auto] content-start gap-y-2 px-2 py-3 duration-150 [&:where(>*)]:col-start-2"
            data-role="user"
        >
            <div className="aui-user-message-content-wrapper relative col-start-2 min-w-0">
                <div className="mb-2">
                    <MessageSenderChip />
                </div>
                <div className="aui-user-message-content peer bg-primary text-primary-foreground flex flex-col gap-2 rounded-2xl px-4 py-2.5 wrap-break-word empty:hidden">
                    <MessagePrimitive.Parts />

                    <UserMessageAttachments />
                </div>
            </div>
        </MessagePrimitive.Root>
    );
};

const EditComposer: FC = () => {
    return (
        <MessagePrimitive.Root className="aui-edit-composer-wrapper mx-auto flex w-full max-w-(--thread-max-width) flex-col px-2 py-3">
            <ComposerPrimitive.Root className="aui-edit-composer-root bg-muted ml-auto flex w-full max-w-[85%] flex-col rounded-2xl">
                <ComposerPrimitive.Input
                    className="aui-edit-composer-input text-foreground min-h-14 w-full resize-none bg-transparent p-4 text-sm outline-none"
                    autoFocus
                />
                <div className="aui-edit-composer-footer mx-3 mb-3 flex items-center gap-2 self-end">
                    <ComposerPrimitive.Cancel
                        render={<Button variant="ghost" size="sm" />}
                    >
                        Cancel
                    </ComposerPrimitive.Cancel>
                    <ComposerPrimitive.Send
                        render={
                            <Button size="sm" className="hover:bg-primary/80" />
                        }
                    >
                        Update
                    </ComposerPrimitive.Send>
                </div>
            </ComposerPrimitive.Root>
        </MessagePrimitive.Root>
    );
};
